import React from 'react';
import { Star, Quote } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import SEO, { generateReviewSchema } from '../components/SEO';

const Reviews = () => {
  const allReviews = [
    {
      id: 1,
      name: "Ryan Wicks",
      rating: 5,
      text: "We hired the RollerTeam 707 for five nights in October 2025 after seeing a recommendation for Millie's Motorhome Hire online. This was our first family experience of motorhoming and we had such a great time! The vehicle was less than 3 years old, low mileage, super clean and with a great spec. The automatic gearbox made even country driving straightforward and when we pitched up the heating, lighting and hot water made every night super comfortable. Both children slept great in the bunk beds as did we on the descending double bed. We will definitely be booking again!",
      date: "October 2025",
      vehicle: "RollerTeam 707"
    },
    {
      id: 2,
      name: "Scott Jennings",
      rating: 5,
      text: "We have just had our first holiday in a MotorHome and it was a wonderful experience. We booked directly through Millie's website and everything worked excellently. Once booked we had great comms from Emily and Jack leading up to our departure. When we picked the motorhome up Emily showed us everything we needed to know and also sent videos incase we experienced any issues. We also had a 24/7 number to call with any problems (fortunately didn't need to use it😊). The motorhome is fantastic, modern with everything you could want and most importantly Emily and Jack made the whole process stress free.",
      date: "October 2025",
      vehicle: "First Motorhome Holiday"
    },
    {
      id: 3,
      name: "Daz Mumford",
      rating: 5,
      text: "Our first time motohoming. Millies were great. Emily looked after us very well and patiently on collection and return. Easy to book, lovely vehicle and had a great time.",
      date: "October 2025",
      vehicle: "First Time Experience"
    },
    {
      id: 4,
      name: "Kazzie Bears",
      rating: 5,
      text: "Just returned from first adventure with a Millie's motorhome hire and can thoroughly recommend. From booking, comms and collecting/returning MH all went smoothly. Jack and Emily (and Jo when we called her…thank you☺️) all very helpful and friendly. We hired the oldest van in their fleet as we liked the layout, there are lots of variables as well as multi berths making a great choice for any couple/family. It is nice to support a small business as opposed to a national MH company and we will definitely do so again!",
      date: "September 2025",
      vehicle: "Various Options Available"
    },
    {
      id: 5,
      name: "Paul Charie",
      rating: 5,
      text: "The team provided excellent service. We were upgraded as our booked van was returned with a fault the previous day. The Campervan features walkthrough on handover was efficient. Videos on key functions were supplied. Van return was straightforward. We recommend and will re-use.",
      date: "July 2025",
      vehicle: "Excellent Customer Service"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO title="Motorhome Hire Reviews | 5-Star Google Rated in Suffolk & Essex" rawTitle url="/reviews"
        description="Read genuine 5-star Google reviews from families who have hired motorhomes from Millie's Motorhome Hire in Suffolk. 26 five-star reviews and counting."
        structuredData={generateReviewSchema(allReviews)} />
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <Badge className="mb-4 bg-yellow-500 text-slate-900 hover:bg-yellow-600 text-lg px-6 py-2">
            5.0 STAR RATING
          </Badge>
          <h1 className="text-5xl font-bold mb-6">Customer Reviews</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
            Read what our customers say about their motorhome hire experience with Millie's
          </p>
          <div className="flex items-center justify-center gap-2 mb-4">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-8 w-8 fill-yellow-400 text-yellow-400" />
            ))}
          </div>
          <p className="text-2xl font-semibold">5.0 out of 5 stars</p>
          <p className="text-slate-400">Based on 25+ Google Reviews</p>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            {allReviews.map((review) => (
              <Card key={review.id} className="hover:shadow-2xl transition-all">
                <CardContent className="p-8">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-bold text-xl mb-1">{review.name}</h3>
                      <p className="text-sm text-slate-600">{review.date}</p>
                    </div>
                    <Quote className="h-10 w-10 text-blue-200" />
                  </div>
                  
                  <div className="flex mb-4">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>

                  {review.vehicle && (
                    <Badge className="mb-4" variant="outline">
                      {review.vehicle}
                    </Badge>
                  )}

                  <p className="text-slate-700 leading-relaxed italic">
                    "{review.text}"
                  </p>

                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-slate-500">Posted on Google Reviews</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold text-blue-600 mb-2">25+</div>
              <p className="text-slate-600">Google Reviews</p>
            </div>
            <div>
              <div className="text-5xl font-bold text-blue-600 mb-2">5.0</div>
              <p className="text-slate-600">Average Rating</p>
            </div>
            <div>
              <div className="text-5xl font-bold text-blue-600 mb-2">100%</div>
              <p className="text-slate-600">Would Recommend</p>
            </div>
            <div>
              <div className="text-5xl font-bold text-blue-600 mb-2">2022</div>
              <p className="text-slate-600">Est. Family Business</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Join Our Happy Customers?</h2>
          <p className="text-xl mb-8 text-blue-100">
            Experience the same excellent service and quality motorhomes
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100" asChild>
              <a href="/motorhomes">Browse Our Fleet</a>
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600" asChild>
              <a href="mailto:milliesmotorhomehire@gmail.com">Contact Us</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
